<template>
  <div>
    <label 
      v-if="label || $slots.default" 
			:class="[labelClass, {'checkbox-label': inputType === 'checkbox', 'checked': isChecked}, checkboxClass]"
      @click="toggleCheckbox"
    >
      <span v-if="label" v-safe-html="label"></span>
      <slot v-else></slot>
    </label>
    <input
      v-if="inputType !== 'checkbox'"
      :type="inputType"
      :class="[inputClass, customInputClass, { 'input--invalid': hasError }]"
      :placeholder="placeholder"
      :value="modelValue"
      :maxlength="maxlength || null"
      :inputmode="inputmode || null"
      :aria-invalid="hasError ? 'true' : 'false'"
      v-bind="inputProps"
      @input="updateValue"
      @blur="handleBlur"
    />
    <input
      v-else
      type="checkbox"
      :class="[inputClass]"
      :checked="isChecked"
      @change="updateValue"
      style="display: none"
    />

    <a
      v-if="infoInInput"
      href="#"
      class="text-end float-right sticky mt-[-3.2rem] text-black underline mr-[1.6rem]"
      @click.prevent="handleInfoClick"
    >{{ infoInInput }}</a>

    <span v-if="hasError" class="input-field__error text-[red] text-[0.75em]">{{ activeErrorMessage }}</span>

  </div>
</template>

<script>
export default {
  name: 'InputField',
  props: {
    inputType: {
      type: String,
      default: 'text'
    },
    inputClass: {
      type: String,
      default: 'w-full h-[4.8rem] px-[1.6rem] py-0 rounded-[10rem] border border-white'
    },
    customInputClass: {
      type: String,
      default: ''
    },
    labelClass: {
      type: String,
      default: 'text-black font-bold pb-4'      
    },
    placeholder: {
      type: String,
      default: ''
    },
    modelValue: {
      type: [String, Boolean],
      default: ''
    },
    label: {
      type: String,
      default: ''
    },
    required: {
      type: Boolean,
      default: false
    },
		infoInInput: {
      type: String,
      default: ""
		},
    errorMessage: {
      type: String,
      default: ''
    },
    inputProps: {
      type: Object,
      default: () => ({})
    },
    maxlength: {
      type: [Number, String],
      default: null
    },
    inputmode: {
      type: String,
      default: null
    },
    onlyDigits: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      isInvalid: false,
      isChecked: this.modelValue
    }
  },
	computed: {
    checkboxClass() {
      if (this.inputType === 'checkbox') {
        return 'block relative font-normal text-[1.4rem] cursor-pointer select-none pl-[3.1rem] before:content-[""] before:absolute before:w-[1.8rem] before:h-[1.8rem] before:border before:rounded-[50%] before:border-solid before:border-black before:left-0 before:top-0';
      }
      return '';
    },
    hasError() {
      return this.isInvalid || !!this.errorMessage;
    },
    activeErrorMessage() {
      if (this.errorMessage) {
        return this.errorMessage;
      }
      if (this.isInvalid) {
        return this.$t('This field is mandatory');
      }
      return '';
    }
  },
  methods: {
    updateValue(event) {
      if (this.inputType === 'checkbox') {
        this.isChecked = event.target.checked;
        this.$emit('update:modelValue', this.isChecked);
        this.isInvalid = this.required && !this.isChecked;
        return;
      }

      let value = event.target.value ?? '';
      if (this.onlyDigits) {
        value = value.replace(/\D+/g, '');
        if (event.target.value !== value) {
          event.target.value = value;
        }
      }

      this.$emit('update:modelValue', value);
      this.$emit('input', value);
      this.isInvalid = this.required && !value.trim();
    },
    handleBlur(event) {
      if (this.inputType === 'checkbox') {
        return;
      }

      let value = event.target.value ?? '';
      if (this.onlyDigits) {
        value = value.replace(/\D+/g, '');
        if (event.target.value !== value) {
          event.target.value = value;
        }
        this.$emit('update:modelValue', value);
      }

      this.$emit('blur', value);
      this.isInvalid = this.required && !value.trim();
    },
    toggleCheckbox() {
      if (this.inputType === 'checkbox') {
        this.isChecked = !this.isChecked;
        this.$emit('update:modelValue', this.isChecked);
        this.isInvalid = this.required && !this.isChecked;
      }
    },
    handleInfoClick() {
      this.$emit('info-click');
    }
  },
  watch: {
    modelValue(newVal) {
      if (this.inputType === 'checkbox') {
        this.isChecked = newVal;
        this.isInvalid = this.required && !this.isChecked;
        return;
      }

      const rawValue = typeof newVal === 'string' ? newVal : '';
      const sanitized = this.onlyDigits ? rawValue.replace(/\D+/g, '') : rawValue;

      if (this.onlyDigits && sanitized !== rawValue) {
        this.$emit('update:modelValue', sanitized);
        return;
      }

      this.isInvalid = this.required && !sanitized.trim();
    }
  }
}
</script>

<style scoped>
.input.is-invalid {
  border-color: red;
}

.input--invalid {
  border-color: #ef4444 !important;
}

.checkbox-label {
  position: relative;
  cursor: pointer;
  padding-left: 25px;
}

.checkbox-label.checked::before {
  background: url('@/assets/images/tic_mark.svg') no-repeat center;
}

.checkbox-label.is-invalid {
  color: red;
}

.checkbox-label.is-invalid::before {
  border: 1px solid #f00 !important;
}

.input-field__error {
  display: block;
  margin-top: 0.4rem;
}
</style>
